import React from "react";
import { useFormikContext } from "formik";
import {
  InputLabel,
  RadioGroupFormik,
  SelectFormik,
  TextInputFormik,
  ToggleFormik,
} from "component-library";
import { FacetType } from "shared-services";
import { useTranslation } from "i18n";

import useFacetOptions from "../../../../../../hooks/useFacetOptions";
import { IAboutYouFormInformationModel } from "../../../../../../formModels/AboutYouFormModel";
import {
  useAdditionalGenderOptions,
  TRANSGENDER_MALE_FACET_ID,
  checkIsTransGenderFacetId,
} from "../../../../../../hooks/useAdditionalGenderOptions";

interface IGenderSelectionProps {
  names: IGenderSelectionFieldNames;
  pageTexts: IGenderSelectionTexts;
  placeholder?: string;
  tooltipComponent?: () => React.JSX.Element;
}

interface IGenderSelectionFieldNames {
  main: string;
  customOrNonBinaryGender: string;
  furtherInformation: string;
  showGenderOnProfile: string;
}

interface IGenderSelectionTexts {
  findMeAsLabel: string;
  findMeAsInSearchResultsLabel: string;
  furtherInformationLabel: string;
  genderToBeDisplayedLabel: string;
}

const GenderSelection: React.FC<
  React.PropsWithChildren<IGenderSelectionProps>
> = ({ names, placeholder, pageTexts, tooltipComponent }) => {
  const { t } = useTranslation();
  const {
    facetOptions: genderOptions,
    loadingFacetOptions: loadingGenderOptions,
  } = useFacetOptions(FacetType.Gender, true);
  const { values } = useFormikContext<IAboutYouFormInformationModel>();
  const genderValue = values.gender;
  const customGenderValue = values.customOrNonBinaryGender;

  const { additionalGenderOptions, showOptions } =
    useAdditionalGenderOptions(genderValue);

  const showTransgenderDescription = checkIsTransGenderFacetId(genderValue);

  const selectedRadioOption = additionalGenderOptions.find(
    (option) => option.value == customGenderValue
  );
  const showWarningMessage = selectedRadioOption?.value === 558;

  if (loadingGenderOptions) return null;

  return (
    <>
      <SelectFormik
        name={names.main}
        label={pageTexts.findMeAsLabel}
        placeholder={placeholder}
        options={genderOptions}
        tooltipComponent={tooltipComponent}
      />
      {values.gender && (
        <>
          {showTransgenderDescription && (
            <div className="c-info-section">
              <div className="mb-5 -mt-4 ">
                <div className="p-2 -mt-6 bg-color-neutral-three">
                  {t("joining:graduate.details.page.form.transgender.descr", {
                    gender:
                      genderValue === TRANSGENDER_MALE_FACET_ID
                        ? t("common:label.male")
                        : t("common:label.female"),
                  })}
                </div>
              </div>
            </div>
          )}

          {showOptions && (
            <div className="mb-5 -mt-4">
              <InputLabel label={pageTexts.findMeAsInSearchResultsLabel} bold />

              <RadioGroupFormik
                name={names.customOrNonBinaryGender}
                radioButtons={additionalGenderOptions}
              />
              {Boolean(customGenderValue) && (
                <div
                  className={`p-2 -mt-6 ${
                    showWarningMessage
                      ? "border bg-red-pale border-red-cardinal"
                      : "bg-color-neutral-three"
                  }`}
                >
                  {selectedRadioOption?.description}
                </div>
              )}
            </div>
          )}

          <TextInputFormik
            name={names.furtherInformation}
            label={pageTexts.furtherInformationLabel}
            placeholder="e.g. Gender fluid"
            isOptional
            maxLength={75}
          />

          <div className="mb-6">
            <ToggleFormik
              name={names.showGenderOnProfile}
              label={pageTexts.genderToBeDisplayedLabel}
              className="font-semibold"
            />
            <p
              className="mt-4 pl-[62px] md:mt-0"
              dangerouslySetInnerHTML={{
                __html: values?.displayGender
                  ? t("common:performer.profile.gender.showGender")
                  : t("common:performer.profile.gender.hideGender"),
              }}
            />
          </div>
        </>
      )}
    </>
  );
};

export default GenderSelection;
